import { useMemo } from 'react';

import { accountStatusOptions } from '@/constants';
import type {
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
} from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AccountResponse } from '@/services/accounts';

export function useAccountLookupColumnDefs() {
  const enableEmbryoOwnerAltSearch = useFeatureFlag('Alternate_Account_Lookup');

  return useMemo<ColDef<AccountResponse>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Account Name',
        filter: 'agTextColumnFilter',
        minWidth: 270,
      },
      {
        field: 'code',
        headerName: 'Ranch ID',
        filter: 'agTextColumnFilter',
        maxWidth: 120,
      },
      {
        colId: 'primaryContact',
        headerName: 'Primary Contact',
        filter: 'agTextColumnFilter',
        valueGetter: (params: ValueGetterParams<AccountResponse>) =>
          params.data?.primaryContact?.fullName ?? '',
      },
      {
        field: 'emailAddress1',
        headerName: 'Email',
        filter: 'agTextColumnFilter',
        minWidth: 220,
      },
      {
        colId: 'csr',
        headerName: 'CSR',
        filter: 'agTextColumnFilter',
        valueGetter: (params: ValueGetterParams<AccountResponse>) =>
          params.data?.customerServiceRepresentative?.fullName ?? '',
      },
      {
        field: 'accountStatus',
        headerName: 'Account Status',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [...accountStatusOptions.map((o) => o.value), null],
          valueFormatter: (
            params: ValueFormatterParams<AccountResponse, number | null>,
          ) => {
            if (params.value == null) return '(Blanks)';
            const option = accountStatusOptions.find(
              (o) => o.value === Number(params.value),
            );
            return option?.label ?? '';
          },
        },
        valueFormatter: (
          params: ValueFormatterParams<AccountResponse, number | null>,
        ) => {
          if (params.value == null) return '';
          const option = accountStatusOptions.find(
            (o) => o.value === params.value,
          );
          return option?.label ?? '';
        },
      },
      {
        colId: 'city',
        headerName: 'City',
        filter: 'agTextColumnFilter',
        valueGetter: (params: ValueGetterParams<AccountResponse>) =>
          params.data?.ranchAddress?.city ?? '',
      },
      {
        colId: 'stateOrProvince',
        headerName: 'State/Province',
        filter: 'agTextColumnFilter',
        maxWidth: 165,
        valueGetter: (params: ValueGetterParams<AccountResponse>) =>
          params.data?.ranchAddress?.stateOrProvince ?? '',
      },
      {
        field: 'stateCode',
        headerName: 'Status',
        maxWidth: 120,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['0', '1'],
          valueFormatter: (
            params: ValueFormatterParams<AccountResponse, string>,
          ) => (Number(params.value) === 0 ? 'Active' : 'Inactive'),
        },
        valueFormatter: (
          params: ValueFormatterParams<AccountResponse, number>,
        ) => (params.value === 0 ? 'Active' : 'Inactive'),
      },
      ...(enableEmbryoOwnerAltSearch
        ? ([
            {
              field: 'phoneNumber',
              headerName: 'Phone Number',
              filter: 'agTextColumnFilter',
              minWidth: 170,
            },
            {
              field: 'ranchAddress.composite',
              headerName: 'Ranch Address',
              filter: 'agTextColumnFilter',
              minWidth: 280,
              valueGetter: (params: ValueGetterParams<AccountResponse>) =>
                params.data?.ranchAddress?.composite ?? '',
            },
            {
              field: 'billingAddress.composite',
              headerName: 'Billing Address',
              filter: 'agTextColumnFilter',
              minWidth: 280,
              valueGetter: (params: ValueGetterParams<AccountResponse>) =>
                params.data?.billingAddress?.composite ?? '',
            },
          ] as ColDef<AccountResponse>[])
        : []),
    ],
    [enableEmbryoOwnerAltSearch],
  );
}
